/**
 * TableGrid — live occupancy board for the host stand.
 *
 * Renders N table cards (from business.tables_count). Each is either:
 *   - OPEN  → tap opens a "seat next waiting guest" picker
 *   - OCCUPIED → shows the party's name/size, seated duration, tap to clear
 *
 * The two taps (seat, clear) generate the raw seating_events that Phase 3's
 * rolling estimator will average. If tables_count is 0 (fresh account) we
 * render a friendly setup nudge instead of an empty grid.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, X, ForkKnife, ArrowRight, Chair } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const formatMinutes = (n) => {
  if (n == null) return "";
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

export default function TableGrid({ business, waitingGuests, onChange }) {
  const [state, setState] = useState({ tables_count: 0, tables: [], open_count: 0 });
  const [stats, setStats] = useState({ events_count: 0, avg_minutes: null });
  const [pickerOpen, setPickerOpen] = useState(null); // table_number the host is seating
  const [walkinParty, setWalkinParty] = useState(2);
  const [walkinName, setWalkinName] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r1 = await api.get("/tables/status");
      setState(r1.data);
      const r2 = await api.get("/tables/turn-time-stats");
      setStats(r2.data);
    } catch (e) {
      console.debug("tables status load failed:", e?.message);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const seat = async (tableNumber, guestId) => {
    setBusy(`seat-${tableNumber}`);
    try {
      await api.post("/tables/seat", { guest_id: guestId, table_number: tableNumber });
      toast.success(`Seated at Table ${tableNumber}.`);
      setPickerOpen(null);
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seat failed");
    }
    setBusy(null);
  };

  const seatWalkin = async (tableNumber) => {
    setBusy(`walkin-${tableNumber}`);
    try {
      await api.post("/tables/seat-walkin", {
        table_number: tableNumber,
        party_size: Math.max(1, parseInt(walkinParty) || 2),
        name: walkinName.trim(),
      });
      toast.success(`Walk-in seated at Table ${tableNumber}.`);
      setPickerOpen(null);
      setWalkinName("");
      setWalkinParty(2);
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seat failed");
    }
    setBusy(null);
  };

  const complete = async (tableNumber) => {
    setBusy(`complete-${tableNumber}`);
    try {
      const r = await api.post(`/tables/complete/${tableNumber}`);
      const mins = r?.data?.meal_duration_minutes;
      toast.success(mins != null ? `Party done — ${formatMinutes(mins)} meal. Bus the table next.` : "Party marked complete.");
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Complete failed");
    }
    setBusy(null);
  };

  const clear = async (tableNumber) => {
    setBusy(`clear-${tableNumber}`);
    try {
      const r = await api.post(`/tables/clear/${tableNumber}`);
      const busy = r?.data?.total_busy_minutes;
      toast.success(busy != null ? `Table ${tableNumber} ready — ${formatMinutes(busy)} total busy.` : `Table ${tableNumber} ready.`);
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    }
    setBusy(null);
  };

  // Empty state: no tables_count set yet.
  if (!state.tables_count) {
    return (
      <div className="paper-card p-6 mb-6 border-l-4 border-[#F5C543]" data-testid="tables-setup-empty">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#F5C543]/25 flex items-center justify-center flex-shrink-0">
            <ForkKnife size={20} color="#B45309" weight="fill" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-extrabold text-lg text-[#121914] mb-1">Enable the live table board</div>
            <p className="text-sm text-[#5C6B61] leading-snug mb-3">
              Once you tell us how many tables you have, this section becomes a tap-to-seat / tap-to-clear grid. Every seat and clear event feeds the wait-time estimator automatically — no more typing in turn times that drift from reality.
            </p>
            <a href="/dashboard/settings#tables-count" className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-1.5" data-testid="tables-setup-go">
              Set my table count <ArrowRight size={14} weight="bold" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-card p-6 mb-6" data-testid="table-grid">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="overline text-[#1E3A2F]">Live table board</div>
          <div className="text-sm text-[#5C6B61] mt-0.5">
            <span className="font-semibold text-[#121914]" data-testid="tables-open-count">{state.open_count}</span> open ·{" "}
            <span className="font-semibold text-[#D96C5B]">{state.occupied_count || 0}</span> occupied ·{" "}
            <span className="font-semibold text-[#B45309]">{state.clearing_count || 0}</span> clearing
            {stats.events_count > 0 && (
              <span className="ml-3 text-xs">avg meal {formatMinutes(stats.avg_meal_minutes)} · avg busy {formatMinutes(stats.avg_busy_minutes)} ({stats.events_count})</span>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {state.tables.map((t) => {
          const isOpen = t.status === "open";
          const isOccupied = t.status === "occupied";
          const isClearing = t.status === "clearing";
          const doing = busy === `clear-${t.table_number}` || busy === `complete-${t.table_number}`;
          const onTap = () => {
            if (isOpen) return setPickerOpen(t.table_number);
            if (isOccupied) return complete(t.table_number);
            if (isClearing) return clear(t.table_number);
          };
          const borderCls = isOpen
            ? "border-[#1E3A2F]/20 bg-[#C2D1C1]/25 hover:border-[#1E3A2F]/40 hover:bg-[#C2D1C1]/45"
            : isOccupied
              ? "border-[#D96C5B]/30 bg-[#F1D7D2]/40 hover:border-[#D96C5B]/60"
              : "border-[#F5C543] bg-[#F5C543]/25 hover:bg-[#F5C543]/40";
          const dotCls = isOpen ? "bg-[#1E3A2F]" : isOccupied ? "bg-[#D96C5B]" : "bg-[#B45309]";
          return (
            <button
              key={t.table_number}
              type="button"
              onClick={onTap}
              disabled={doing}
              data-testid={`table-${t.table_number}`}
              data-status={t.status}
              className={`relative rounded-xl border-2 p-3 text-left transition-all min-h-[92px] flex flex-col justify-between ${borderCls}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B61]">Table {t.table_number}</div>
                <div className={`w-2 h-2 rounded-full ${dotCls}`} />
              </div>
              {isOpen && (
                <div>
                  <div className="text-lg font-heading font-extrabold text-[#1E3A2F]">Open</div>
                  <div className="text-[10px] text-[#5C6B61] mt-0.5 inline-flex items-center gap-0.5"><Plus size={10} weight="bold" /> tap to seat</div>
                </div>
              )}
              {isOccupied && (
                <div>
                  <div className="text-sm font-semibold text-[#121914] truncate">{t.guest_name || "—"}</div>
                  <div className="text-[10px] text-[#5C6B61] mt-0.5 flex items-center gap-1">
                    <Chair size={10} weight="fill" /> {t.party_size || "?"} · {formatMinutes(t.seated_since_min)}
                    <span className="ml-auto font-semibold text-[#D96C5B]">tap: done</span>
                  </div>
                </div>
              )}
              {isClearing && (
                <div>
                  <div className="text-sm font-semibold text-[#B45309] truncate">Clearing…</div>
                  <div className="text-[10px] text-[#5C6B61] mt-0.5 flex items-center gap-1">
                    ready in {formatMinutes(t.clearing_since_min)}
                    <span className="ml-auto font-semibold text-[#B45309]">tap: ready</span>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Active tables list — scannable view of every table currently in use,
          with explicit Complete / Ready buttons. Same data as the grid above,
          but mobile-friendlier when the host stand is watching turnover. */}
      {(state.occupied_count > 0 || state.clearing_count > 0) && (
        <div className="mt-6 pt-4 border-t border-[#E1DFD5]" data-testid="active-tables-list">
          <div className="overline mb-2">Active tables ({(state.occupied_count || 0) + (state.clearing_count || 0)})</div>
          <div className="space-y-2">
            {state.tables.filter((t) => t.status !== "open").map((t) => {
              const isOccupied = t.status === "occupied";
              return (
                <div
                  key={`active-${t.table_number}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${isOccupied ? "bg-[#F1D7D2]/30 border-[#D96C5B]/30" : "bg-[#F5C543]/15 border-[#F5C543]/40"}`}
                  data-testid={`active-table-${t.table_number}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-heading font-extrabold text-sm ${isOccupied ? "bg-[#D96C5B]/25 text-[#A5382A]" : "bg-[#F5C543]/40 text-[#78350F]"}`}>{t.table_number}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#121914] truncate">{t.guest_name || "Walk-in"}</div>
                    <div className="text-[11px] text-[#5C6B61]">
                      {isOccupied ? (
                        <>Party {t.party_size || "?"} · seated {formatMinutes(t.seated_since_min)} ago</>
                      ) : (
                        <>Clearing · finished {formatMinutes(t.clearing_since_min)} ago</>
                      )}
                    </div>
                  </div>
                  {isOccupied ? (
                    <button
                      type="button"
                      onClick={() => complete(t.table_number)}
                      disabled={busy === `complete-${t.table_number}`}
                      className="btn-ghost !py-1.5 !px-3 text-xs"
                      data-testid={`active-complete-${t.table_number}`}
                    >
                      Mark complete
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => clear(t.table_number)}
                      disabled={busy === `clear-${t.table_number}`}
                      className="btn-primary !py-1.5 !px-3 text-xs"
                      data-testid={`active-clear-${t.table_number}`}
                    >
                      Table ready
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={pickerOpen !== null} onOpenChange={(o) => !o && setPickerOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="picker-title">Seat at Table {pickerOpen}</DialogTitle>
          </DialogHeader>

          {/* Walk-in — quick path when a guest walks in without being on the list. */}
          <div className="p-3 rounded-lg bg-[#F5C543]/15 border border-[#F5C543]/40 mb-3" data-testid="picker-walkin">
            <div className="overline text-[#B45309] mb-2">Walk-in</div>
            <div className="grid grid-cols-[1fr_80px] gap-2 mb-2">
              <input
                className="input-field !py-2 text-sm"
                placeholder="Name (optional)"
                value={walkinName}
                onChange={(e) => setWalkinName(e.target.value)}
                data-testid="picker-walkin-name"
              />
              <input
                type="number"
                min={1}
                max={99}
                className="input-field !py-2 text-sm"
                placeholder="Party"
                value={walkinParty}
                onChange={(e) => setWalkinParty(e.target.value)}
                data-testid="picker-walkin-party"
              />
            </div>
            <button
              type="button"
              onClick={() => seatWalkin(pickerOpen)}
              disabled={busy === `walkin-${pickerOpen}`}
              className="btn-primary w-full justify-center !py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="picker-walkin-submit"
            >
              {busy === `walkin-${pickerOpen}` ? "Seating…" : `Seat walk-in at Table ${pickerOpen}`}
            </button>
          </div>

          <div className="overline text-[#5C6B61] mb-2">Or seat from waitlist</div>
          {waitingGuests?.length ? (
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {waitingGuests.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  disabled={busy === `seat-${pickerOpen}`}
                  onClick={() => seat(pickerOpen, g.id)}
                  data-testid={`picker-guest-${g.id}`}
                  className="w-full text-left p-3 rounded-lg border border-[#E1DFD5] hover:border-[#1E3A2F]/40 hover:bg-[#F7F6F2]"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-[#121914]">{g.name}</div>
                      <div className="text-xs text-[#5C6B61]">Party {g.party_size} · {g.status}</div>
                    </div>
                    <ArrowRight size={16} color="#1E3A2F" weight="bold" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-[#5C6B61] text-xs">Nobody on the waitlist — use the walk-in path above.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
